import { useTranslation } from 'react-i18next';
import { useCompareConfigStore } from '@/store/compare-config-store';
import { CompareConfigPanel } from './CompareConfigPanel';
import { formatPeriod } from './format-period';

/**
 * Compare-mode banner + config panel (D-020). Banner shows the active
 * comparison labels once configured ("Mart 2025 vs Mart 2024"); the
 * panel below it lets the user pick / change them. Until the user
 * sets two periods, the banner renders a one-line nudge to the
 * panel.
 *
 * Pulled out of App.tsx during R-6.
 */
export function CompareBanner(): JSX.Element {
  const { t } = useTranslation();
  const compareConfig = useCompareConfigStore((s) => s.config);

  return (
    <>
      {compareConfig ? (
        <div
          role="status"
          className="mb-3 flex items-stretch rounded-lg overflow-hidden"
          style={{
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 1px 2px var(--color-shadow)',
          }}
        >
          <div
            className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5"
            style={{ borderRight: '1px solid var(--color-border)' }}
          >
            <div
              className="shrink-0 rounded"
              style={{ width: 6, height: 30, background: 'var(--color-accent)' }}
              aria-hidden
            />
            <div className="flex flex-col">
              <span
                className="text-[9px] font-bold uppercase"
                style={{ color: 'var(--color-muted)', letterSpacing: '0.08em' }}
              >
                {t('mode.panel.periodA')}
              </span>
              <span
                className="text-[13px] font-semibold"
                style={{ color: 'var(--color-fg)' }}
              >
                {formatPeriod(t, compareConfig.granularity, compareConfig.periodA)}
              </span>
            </div>
          </div>
          <div
            className="flex items-center justify-center text-[11px] font-bold uppercase"
            style={{
              padding: '0 14px',
              background: 'var(--color-surface)',
              color: 'var(--color-muted)',
              letterSpacing: '0.1em',
            }}
          >
            {t('mode.panel.vs')}
          </div>
          <div
            className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5"
            style={{ borderLeft: '1px solid var(--color-border)' }}
          >
            <div
              className="shrink-0 rounded"
              style={{ width: 6, height: 30, background: 'var(--color-warning)' }}
              aria-hidden
            />
            <div className="flex flex-col">
              <span
                className="text-[9px] font-bold uppercase"
                style={{ color: 'var(--color-muted)', letterSpacing: '0.08em' }}
              >
                {t('mode.panel.periodB')}
              </span>
              <span
                className="text-[13px] font-semibold"
                style={{ color: 'var(--color-fg)' }}
              >
                {formatPeriod(t, compareConfig.granularity, compareConfig.periodB)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div
          role="status"
          className="mb-3 px-3 py-2 rounded-md text-xs"
          style={{
            background:
              'color-mix(in srgb, var(--color-accent) 12%, var(--color-surface))',
            border: '1px solid var(--color-accent)',
            color: 'var(--color-fg)',
          }}
        >
          {t('mode.compareBanner')}
        </div>
      )}
      <CompareConfigPanel />
    </>
  );
}
